import { warehouseApi as api } from "./warehouseApi";
const injectedRtkApi = api.injectEndpoints({
  endpoints: (build) => ({
    searchBin: build.query<SearchBinApiResponse, SearchBinApiArg>({
      query: (queryArg) => ({ url: `/bins/search/${queryArg.searchText}` })
    }),
    getBinsByFlaggedReason: build.query<
      GetBinsByFlaggedReasonApiResponse,
      GetBinsByFlaggedReasonApiArg
    >({
      query: (queryArg) => ({
        url: `/bins/autostore/grid/${queryArg.autostoreGridId}/by-flagged-reason/${queryArg.flaggedReason}`
      })
    }),
    maintenanceFlaggedBinCounts: build.query<
      MaintenanceFlaggedBinCountsApiResponse,
      MaintenanceFlaggedBinCountsApiArg
    >({
      query: (queryArg) => ({
        url: `/bins/autostore/grid/${queryArg.autostoreGridId}/maintenance/flagged-bin-counts`,
        params: {
          temperatureZone: queryArg.temperatureZone
        }
      })
    }),
    getBinCompartments: build.query<
      GetBinCompartmentsApiResponse,
      GetBinCompartmentsApiArg
    >({
      query: (queryArg) => ({
        url: `/bins/autostore/compartments`,
        params: {
          gridId: queryArg.gridId,
          searchText: queryArg.searchText,
          limit: queryArg.limit
        }
      })
    })
  }),
  overrideExisting: false
});
export { injectedRtkApi as binsApi };
export type SearchBinApiResponse = /** status 200 OK */ SearchBinRecord[];
export type SearchBinApiArg = {
  searchText: string;
};
export type GetBinsByFlaggedReasonApiResponse =
  /** status 200 OK */ AutostoreBin[];
export type GetBinsByFlaggedReasonApiArg = {
  /** Autostore grid Id */
  autostoreGridId: string;
  flaggedReason: FlaggedReason;
};
export type MaintenanceFlaggedBinCountsApiResponse =
  /** status 200 OK — counts flagged bins for bin maintenance using the same flag exclusion rules as inventory-movement scheduling (excluding food-safety-only and inaccessible-flag bins). Unlike the scheduler, includes bins with no on-hand inventory; bins with any committed pick quantity are excluded. `byTemperatureZone` is populated when matches exist; `byFlagType` requires the `temperatureZone` query parameter.
   */ MaintenanceFlaggedBinCountsResponse;
export type MaintenanceFlaggedBinCountsApiArg = {
  /** Autostore grid Id */
  autostoreGridId: string;
  /** When set, `byFlagType` counts flagged bins' flags only within this warehouse temperature zone (`Ambient`, `Chilled`, or `Frozen`). */
  temperatureZone?: "Ambient" | "Chilled" | "Frozen";
};
export type GetBinCompartmentsApiResponse =
  /** status 200 OK */ GetCompartmentsRecord[];
export type GetBinCompartmentsApiArg = {
  /** Autostore Grid Id */
  gridId: string;
  /** The search text to filter bins by */
  searchText: string;
  /** How many search results to return */
  limit?: number | null;
};
export type SearchBinRecord = {
  bin_id: string;
  bin_type: string;
  location?: string;
  temperature_zone?: string;
  autostore_compartment_number?: number | null;
};
export type FlaggedReason =
  | "dirty"
  | "damaged"
  | "misconfigured"
  | "scheduled"
  | "bin locked"
  | "bin removed"
  | "exterior maintenance"
  | "food safety";
export type AutostoreBin = {
  binId: string;
  autostoreBinId: number;
  autostoreGridId: string;
  binConfigurationId: string;
  content: number;
  isFlagged: boolean;
  flaggedReason: FlaggedReason[];
  flaggedTime?: string | null;
};
export type MaintenanceFlaggedBinCountItem = {
  /** Under `byTemperatureZone`, the warehouse temperature-zone name (`Ambient`, `Chilled`, `Frozen`, or `Unknown`). Under `byFlagType`, a flagged-reason string (see `FlaggedReason`).
   */
  label: string;
  count: number;
};
export type MaintenanceFlaggedBinCountsResponse = {
  /** Count of maintenance flagged bins per warehouse temperature zone. Empty when no bins match. */
  byTemperatureZone: MaintenanceFlaggedBinCountItem[];
  /** Count of flag reasons on maintenance flagged bins in the requested temperature zone (one count per flag on a bin, so a bin with multiple flags contributes to multiple entries). Empty when `temperatureZone` is omitted or no bins match the zone.
   */
  byFlagType: MaintenanceFlaggedBinCountItem[];
};
export type GetCompartmentsRecord = {
  autostore_bin_number: number;
  warehouse_bin_ids: string[];
};
export const {
  useSearchBinQuery,
  useGetBinsByFlaggedReasonQuery,
  useMaintenanceFlaggedBinCountsQuery,
  useGetBinCompartmentsQuery
} = injectedRtkApi;
